import { DeviceTypeConfigurations, ThirdPartyDeviceType } from "verdi-common";
import { DeviceConfiguration } from "verdi-common/Configurations/DeviceConfiguration";
import { hourMs } from "verdi-common/timeConstants";
import { SessionHandler } from "verdi-web-client/APICommands/SessionHandler";

import type { ModelBase } from "verdi-web-client/Models/ModelBase";

import { OPERATING_MODE } from "@/services/device-attributes/attributes/operating-mode/definitions";
/**
 * TODO: find a better home for this file. Probably not suited to the mapEntities folder.
 */
import {
    badgeColorMapping,
    disabledIconColorMapping,
    markerVariantStyleMapping,
    notesColorMapping,
} from "../../../features/map-markers/device-marker/configs/styles";
import {
    BADGE_VARIANT,
    MARKER_VARIANT,
    NOTES_OVERRIDE_COLOR_VARIANT,
    WARNING_STATUS,
} from "../../../features/map-markers/device-marker/configs/variants";
import { BadgeParams, ColorParams, ProgressRingParams } from "../../../features/map-markers/device-marker/types";
import { isValve } from "../../device-attributes/attributes/general";
import { isSomeValveOpen } from "../../device-attributes/attributes/irrigation/helpers";
import { getOperatingMode, isDeviceInManualMode } from "../../device-attributes/attributes/operating-mode/getters";
import { composeDeviceAttributes } from "../../device-attributes/device-attributes";
import { isThirdPartyGateway } from "../../device-attributes/configurations";
import {
    NOTES_CUSTOM_ERROR_PREFIX,
    NOTES_CUSTOM_WARNING_PREFIX,
    NOTES_DISABLED_DEVICE_TEXT,
    NOTES_OVERRIDE_COLOR_PREFIX,
    NOTES_SUPPRESS_WARNINGS_TEXT,
} from "../icon-manager/core/notes";
import type MapEntityBase from "./map-entity-base";

interface WarningsParams {
    model: MapEntityBase["model"];
    type: DeviceConfiguration["type"];
}

export const WARNING_STATUS_PRIORITY = {
    [WARNING_STATUS.WARNING]: 1,
    [WARNING_STATUS.ERROR]: 2,
} as const;

export interface WarningsObj {
    status: WARNING_STATUS | undefined;
    warningText: string | undefined;
    warningToShow:
        | {
              warningType: string;
              warningTitle: string;
              warningLevel: number;
              warningTypePriority: number;
              warningVariant?: string;
              warningParams?: Record<string, string | number>;
          }
        | undefined;
    isSensorOutOfDate: boolean | undefined;
    manualModeWarning: boolean | undefined;
}

const RECENT_DEVICE_CREATION_TRESHOLD = hourMs * 2;

export const makeWarnings = ({ model, type }: WarningsParams): WarningsObj => {
    const warnings: WarningsObj = {
        status: undefined,
        warningText: undefined,
        warningToShow: undefined,
        manualModeWarning: undefined,
        isSensorOutOfDate: undefined,
    };

    /**
     * No warnings for:
     * - seed devices
     * - recently created devices (often false positive error for first hours)
     * - devices that are disabled or suppressed via notes
     * - an admin account has used their admin-only "hideRedDevicesOnAdmin" in the device settings menu
     */
    const recentlyCreated = model.createdAt && Date.now() - model.createdAt.valueOf() < RECENT_DEVICE_CREATION_TRESHOLD;
    if (
        DeviceTypeConfigurations[type]?.generation === "seed" ||
        disabledByNotes(model.notes) ||
        recentlyCreated ||
        suppressWarningsByNotes(model.notes) ||
        SessionHandler.currentUserObject?.webPreferences?.hideRedDevicesOnAdmin
    ) {
        return warnings;
    }

    const allWarnings = model.warnings || [];
    warnings.manualModeWarning = allWarnings.some((w: any) => w.warningType === "Manual Override Engaged");

    // Only raise a visible warning status (badge) for warnings with level > 0 (WARNING or ERROR).
    // INFO-level warnings (level 0, e.g. Manual Override Engaged) are informational and do not
    // produce a notification badge.
    const actionableWarnings = allWarnings.filter((w: any) => w.warningLevel > 0);
    if (actionableWarnings.length > 0) {
        warnings.status = actionableWarnings[0].warningLevel > 1 ? WARNING_STATUS.ERROR : WARNING_STATUS.WARNING;
        warnings.warningText = actionableWarnings[0].warningTitle;
        warnings.warningToShow = actionableWarnings[0];
    }

    // Notes warnings have lower priority than normal device warnings/errors
    if (!warnings.warningText && model.notes) {
        if (warningByNotes(model.notes)) {
            warnings.warningText = "check notes";
            warnings.status = WARNING_STATUS.WARNING;
            warnings.warningToShow = {
                warningType: "custom warning",
                warningTitle: "check notes",
                warningLevel: 1,
                warningTypePriority: 1,
            };
        } else if (errorByNotes(model.notes)) {
            warnings.warningText = "check notes";
            warnings.status = WARNING_STATUS.ERROR;
            warnings.warningToShow = {
                warningType: "custom error",
                warningTitle: "check notes",
                warningLevel: 2,
                warningTypePriority: 1,
            };
        }
    }

    return warnings;
};

export const shouldExpand = ({
    model,
    warningToShow,
    warningStatus,
}: {
    model: MapEntityBase["model"];
    warningToShow: WarningsObj["warningToShow"];
    warningStatus: WarningsObj["status"];
}) => {
    const isManualMode = isDeviceInManualMode({ model });
    const hasOpenValve = isSomeValveOpen({ model });
    const { isManualModeTransitioning } = getOperatingMode({ model });
    return (
        isManualMode ||
        hasOpenValve ||
        isManualModeTransitioning ||
        (warningToShow !== undefined && warningStatus !== undefined)
    );
};

export const disabledByNotes = (notes?: string) => notes?.toLowerCase().includes(NOTES_DISABLED_DEVICE_TEXT);
export const suppressWarningsByNotes = (notes?: string) => notes?.toLowerCase().includes(NOTES_SUPPRESS_WARNINGS_TEXT);
export const warningByNotes = (notes?: string) => notes?.toLowerCase().includes(NOTES_CUSTOM_WARNING_PREFIX);
export const errorByNotes = (notes?: string) => notes?.toLowerCase().includes(NOTES_CUSTOM_ERROR_PREFIX);

export const makeColorParams = ({
    deviceType,
    notes,
}: {
    deviceType: DeviceConfiguration["type"] | ThirdPartyDeviceType;
    notes: string;
}): ColorParams => {
    if (disabledByNotes(notes)) {
        return disabledIconColorMapping;
    }

    if (notes) {
        const colors = Object.values(NOTES_OVERRIDE_COLOR_VARIANT);
        const colorVariant = colors.find((c) => notes.toLowerCase().includes(`${NOTES_OVERRIDE_COLOR_PREFIX}${c}`));
        if (colorVariant) {
            return notesColorMapping[colorVariant];
        }
    }

    if (isValve({ deviceType })) {
        return markerVariantStyleMapping[MARKER_VARIANT.VALVE];
    }

    if (isThirdPartyGateway(deviceType)) {
        return markerVariantStyleMapping[MARKER_VARIANT.DRAGINO_GATEWAY];
    }

    return markerVariantStyleMapping[MARKER_VARIANT.SENSOR];
};

/**
 * Progress-ring params for a group marker: binary, one member irrigating is enough to show the ring.
 */
export const groupProgressRingParams = (hasProgressRing: boolean): ProgressRingParams =>
    hasProgressRing
        ? { progressRing: true, progressRingOptions: { spinning: false, percentage: 100 } }
        : { progressRing: false };

/**
 * Determine if a group should show a progress ring
 */
export const makeGroupProgressRingParams = ({ models }: { models: ModelBase[] }): ProgressRingParams =>
    groupProgressRingParams(
        models.some((model) => {
            const { progressRing } = makeProgressRingParams({ model });
            return progressRing;
        }),
    );

export const makeProgressRingParams = ({ model }: { model: MapEntityBase["model"] }): ProgressRingParams => {
    const { operatingMode, irrigation } = composeDeviceAttributes({ model });

    if (operatingMode.isManualModeTransitioning) {
        return {
            progressRing: true,
            progressRingOptions: {
                spinning: true,
                percentage: 0,
            },
        };
    }

    if (irrigation.hasOpenValve) {
        // If the valve is open in manual mode, show a progress ring at 100% regardless of schedule
        if (operatingMode.operatingMode === OPERATING_MODE.MANUAL_OPEN) {
            return {
                progressRing: true,
                progressRingOptions: {
                    spinning: false,
                    percentage: 100,
                },
            };
        }

        // Show progress ring at set percentage if defined
        if (irrigation.percentageScheduledIrrigationCompleted !== undefined) {
            return {
                progressRing: true,
                progressRingOptions: {
                    spinning: false,
                    percentage: irrigation.percentageScheduledIrrigationCompleted,
                },
            };
        }
        // Show progress ring if valve is open for some other reason outside of schedule
        return {
            progressRing: true,
            progressRingOptions: {
                spinning: false,
                percentage: 100,
            },
        };
    }
    return {
        progressRing: false,
        progressRingOptions: undefined,
    };
};

export const makeBadgeParams = ({
    warningStatus,
    size,
}: {
    warningStatus?: WARNING_STATUS;
    size?: number;
}): BadgeParams => {
    if (warningStatus && warningStatus === WARNING_STATUS.ERROR) {
        return {
            badge: true,
            badgeOptions: {
                bgColor: badgeColorMapping[BADGE_VARIANT.ERROR].bg,
                borderColor: badgeColorMapping[BADGE_VARIANT.ERROR].border,
                size,
            },
        };
    }

    if (warningStatus && warningStatus === WARNING_STATUS.WARNING) {
        return {
            badge: true,
            badgeOptions: {
                bgColor: badgeColorMapping[BADGE_VARIANT.WARNING].bg,
                borderColor: badgeColorMapping[BADGE_VARIANT.WARNING].border,
                size,
            },
        };
    }

    return {
        badge: false,
    };
};
